// ─────────────────────────────────────────────
//  TravelingBox — the Timeline page's 🚗 Traveling section (top middle).
//  Renders ONLY while someone is in transit. Mirrors the main board's
//  Traveling bench exactly in data + actions:
//    • entries grouped under "Going to <facility>" headers
//    • ✓ Arrival on the DESTINATION facility (joins this board's roster,
//      clears a stale departed flag from a round trip)
//    • ✕ Cancel Traveling (they return to Unassigned at the origin)
//  Self-contained: its own transfers listener + roster hook, so a CRNA
//  relieved with the Travel destination in the timeline case modal appears
//  here live, labeled with the destination.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useToast } from "../context/ToastContext";
import { useDailyRoster } from "../hooks/useDailyRoster";
import { listenToTransfers, updateTransfer, setDailyRoster, setRosterMemberDeparted } from "../firebase/firestore";
import { facilityLabel } from "../domain/facilities";
import tokens from "../theme/tokens";

const { color, font, radius, space } = tokens;

export default function TravelingBox({ facilityId, date, uid, canManage = false }) {
  const toast = useToast();
  const { roster } = useDailyRoster(facilityId, date);
  const [transfers, setTransfers] = useState([]);

  useEffect(() => {
    if (!facilityId || !date) {
      setTransfers([]);
      return undefined;
    }
    const unsub = listenToTransfers(
      facilityId, date,
      (snap) => setTransfers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("[TravelingBox] transfers listener failed:", e)
    );
    return unsub;
  }, [facilityId, date]);

  if (!transfers.length) return null;

  const arriveTransfer = async (t) => {
    try {
      const members = Array.from(new Map((roster?.crnas || []).filter((m) => m && m.id).map((m) => [m.id, m])).values());
      if (!members.find((m) => m.id === t.providerId)) {
        members.push({ id: t.providerId, name: t.providerName, subRole: t.subRole || "crna" });
      }
      await setDailyRoster(facilityId, date, { crnas: members }, uid);
      await setRosterMemberDeparted(facilityId, date, t.providerId, false, uid);
      await updateTransfer(t.id, { status: "arrived", arrivedAt: new Date().toISOString(), arrivedBy: uid || null });
      toast.success(`${t.providerName} arrived — now available in Unassigned.`);
    } catch (e) {
      console.error("Arrival failed:", e);
      toast.error("Could not complete the arrival.");
    }
  };

  const cancelTransfer = async (t) => {
    try {
      await updateTransfer(t.id, { status: "cancelled", cancelledAt: new Date().toISOString(), cancelledBy: uid || null });
      await setRosterMemberDeparted(t.fromFacilityId, date, t.providerId, false, uid);
      await setRosterMemberDeparted(t.toFacilityId, date, t.providerId, true, uid);
      toast.info(`${t.providerName} is no longer traveling — back in Unassigned at ${facilityLabel(t.fromFacilityId)}.`);
    } catch (e) {
      console.error("Cancel traveling failed:", e);
      toast.error("Could not cancel the transfer.");
    }
  };

  // Group by destination facility → "Going to X" headers.
  const groups = new Map();
  for (const t of transfers) {
    const key = t.toFacilityId || "?";
    if (!groups.has(key)) groups.set(key, { label: t.toFacilityName || facilityLabel(t.toFacilityId), items: [] });
    groups.get(key).items.push(t);
  }

  return (
    <div style={st.wrap} aria-label="Providers traveling between facilities">
      <div style={st.head}>
        <span style={st.title}>🚗 Traveling</span>
        <span style={st.count}>{transfers.length}</span>
      </div>
      <div style={st.groups}>
        {[...groups.values()].map((g) => (
          <div key={g.label} style={st.group}>
            <div style={st.groupLabel}>Going to {g.label}</div>
            {g.items.map((t) => (
              <div key={t.id} style={st.row}>
                <span style={st.name}>
                  {t.providerName}
                  <span style={st.role}> · {(t.subRole || "crna").toUpperCase()}</span>
                </span>
                {canManage && (
                  <div style={{ display: "flex", gap: space.xs }}>
                    {facilityId === t.toFacilityId && (
                      <button style={{ ...st.btn, background: "#0a7d5e", color: "#fff", border: "none" }}
                        title="They're here — join this board's roster"
                        onClick={() => arriveTransfer(t)}>
                        ✓ Arrival
                      </button>
                    )}
                    <button style={st.btn}
                      title="Cancel the transfer — back to Unassigned at the origin facility"
                      onClick={() => cancelTransfer(t)}>
                      ✕ Cancel Traveling
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const st = {
  // Sits INSIDE the Timeline header row (title left · this middle · controls
  // right) — flexes to the middle at the same level, compact padding.
  wrap: { flex: "0 1 560px", minWidth: 280, margin: "0 auto", background: "#fefce8", border: "1px solid #eab308", borderRadius: 10, padding: "8px 12px" },
  head: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  title: { fontSize: font.size.body, fontWeight: font.weight.bold, color: "#854d0e" },
  count: { background: "#fff", border: "1px solid #eab308", color: "#854d0e", borderRadius: 9999, padding: "0 8px", fontSize: font.size.label, fontWeight: font.weight.bold },
  groups: { display: "flex", flexDirection: "column", gap: 8 },
  group: { display: "flex", flexDirection: "column", gap: 4 },
  groupLabel: { fontSize: 11, fontWeight: font.weight.bold, letterSpacing: ".4px", textTransform: "uppercase", color: "#854d0e" },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: "#fff", border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "6px 10px" },
  name: { fontSize: 13, fontWeight: font.weight.semibold, color: color.text.primary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  role: { fontSize: 11, color: color.text.muted, fontWeight: font.weight.regular },
  btn: { background: "transparent", border: `1px solid ${color.border.default}`, color: color.text.secondary, borderRadius: radius.sm, padding: "4px 10px", fontSize: 12, fontWeight: font.weight.bold, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
};
